var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { z } from 'zod';
import { memo } from 'react';
import { FormProvider, useController, useForm, useFormContext } from 'react-hook-form';
import { trpc } from 'root/src/client/v2/trpc/trpc';
import { FormControl, FormHelperText, TextField } from '@mui/material';
import { useModal } from 'root/src/client/v2/common/contexts/modal.context';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import { ModalLayout } from 'root/src/client/v2/common/layouts/ModalLayout';
import { ModalTitle } from 'root/src/client/v2/common/components/modal/ModalTitle';
import { ButtonNew } from 'root/src/client/v2/common/components/form/ButtonNew';
import LoadingCircles from 'root/src/client/v2/common/components/LoadingCircles';
import { BaseTooltip, BaseTooltipContent } from 'root/src/client/v2/common/components/BaseTooltip/BaseTooltip';
import * as BlackCard from 'root/src/client/v2/common/components/BlackCard/BlackCard';
import { twMerge } from 'tailwind-merge';
import { Add } from '@mui/icons-material';
import { useCreatorDaresContext } from 'root/src/client/web/record/CreatorData/CreatorDare/creatorDares.context';
export const CreateCreatorDare = memo(() => {
    const modal = useModal();
    const { dropId, refetch } = useCreatorDaresContext();
    return (<BaseTooltip triggerClassName="flex w-full" tooltip={<BaseTooltipContent>Create Dare For Your Audience</BaseTooltipContent>}>
			<button onClick={() => {
            modal.show(<CreateDareModal dropId={dropId} onCreate={refetch}/>);
        }} type="button" className="hover:bg-primary duration-100 rounded-2xl">
				<BlackCard.Root className={twMerge('dare-card border-2 border-dashed bg-transparent')}>
					<BlackCard.Body className="flex mb-12 justify-center items-center font-bold">
						Create Your Own Dare <Add />
					</BlackCard.Body>
				</BlackCard.Root>
			</button>
		</BaseTooltip>);
});
export const CreateDareModal = memo(({ dropId, onCreate }) => {
    const modal = useModal();
    const form = useForm({
        resolver: zodResolver(CreateDareFormSchema),
        defaultValues: {
            title: '',
            description: '',
        },
    });
    const createEventFulfilledCreatorDareMutation = trpc.dares.createEventFulfilledCreatorDare.useMutation();
    const onSubmit = form.handleSubmit((data) => __awaiter(void 0, void 0, void 0, function* () {
        yield createEventFulfilledCreatorDareMutation.mutateAsync({
            dropId,
            title: data.title,
            description: data.description,
        });
        yield onCreate();
        modal.hide();
        toast.success('Dare created successfully and is pending review.');
    }), (errors) => {
        console.info(errors);
        console.error(errors);
    });
    return (<ModalLayout className="flex flex-col gap-12">
			<ModalTitle className="capitalize">Create your own dare</ModalTitle>
			<FormProvider {...form}>
				<form onSubmit={onSubmit} className="flex sm:w-[500px] flex-col gap-8">
					<TitleField />
					<DescriptionField />
					<FormControl>
						<ButtonNew disabled={form.formState.isSubmitting} className="flex items-center justify-center" type="submit">
							{form.formState.isSubmitting && <LoadingCircles className="flex flex-initial mr-2" width={20} height={20}/>}
							Create Dare
						</ButtonNew>
						<FormHelperText>
							Your dare will be submitted for review and will be available once approved.
						</FormHelperText>
						<FormHelperText>** Please note that all dares must comply with our community guidelines.</FormHelperText>
						<FormHelperText>** Dare can be disabled at any time by the creator/moderator.</FormHelperText>
					</FormControl>
				</form>
			</FormProvider>
		</ModalLayout>);
});
export const CreateDareFormSchema = z.object({
    title: z.string().min(1, 'Title is required').max(50, 'Title must be less than 50 characters'),
    description: z.string().min(1, 'Description is required').max(200, ' Description must be less than 200 characters'),
});
export const TitleField = memo(() => {
    var _a;
    const form = useFormContext();
    const { field, fieldState } = useController({
        name: 'title',
        control: form.control,
    });
    return (<FormControl className="w-full" error={!!fieldState.error}>
			<TextField {...field} label="Title" variant="outlined" helperText={((_a = fieldState.error) === null || _a === void 0 ? void 0 : _a.message) || 'This will be the title of your dare, keep it short and catchy.'} required/>
		</FormControl>);
});
export const DescriptionField = memo(() => {
    var _a;
    const form = useFormContext();
    const { field, fieldState } = useController({
        name: 'description',
        control: form.control,
    });
    return (<FormControl className="w-full" error={!!fieldState.error}>
			<TextField {...field} label="Description" variant="outlined" multiline rows={4} helperText={((_a = fieldState.error) === null || _a === void 0 ? void 0 : _a.message) || 'Provide a brief description of your dare.'} required/>
		</FormControl>);
});
